<script setup>
import { ref, onMounted } from 'vue';

let stripe = null;
const loading = ref(true);
const csi = ref(null);
const userEmail = ref(null);
const subscriptionCancelationUndone = ref(false);
const subscriptionCanceled = ref(false);
const paymentMethodUpdated = ref(false);
const defaultPaymentMethodUpdated = ref(false);

const props = defineProps({
    customerId: {
        type: String,
        default: null
    },
    memberName: {
        type: String,
        default: null
    }
});

async function setCustomerDefaultPaymethod(sessionId) {
    loading.value = true;

    await fetch(`/Umbraco/Api/Subscription/SetStripeCustomerDefaultPaymentMethod?stripeSetupSessionId=${sessionId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                defaultPaymentMethodUpdated.value = true;
                getCustomerSubscriptionInfo(props.customerId);
            }
        });
}

async function updatePaymentMethod() {
    await fetch(`/Umbraco/Api/Subscription/GetUpdatePaymentMethodSessionId?customerId=${props.customerId}&subscriptionId=${csi.value.subscriptionId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                stripe.redirectToCheckout({
                    sessionId: data.sessionId
                });
            }
        });
}

async function getCustomerSubscriptionInfo(customerId) {
    await fetch(`/Umbraco/Api/Customer/GetCustomerSubscriptionInfo?stripeCustomerId=${customerId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => response.json())
        .then(data => {
            csi.value = data;
            loading.value = false;
        });
}

async function cancelSubscription() {
    loading.value = true;

    await fetch(`/Umbraco/Api/Subscription/CancelSubscription?customerId=${props.customerId}&subscriptionId=${csi.value.subscriptionId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                paymentMethodUpdated.value = false;
                subscriptionCancelationUndone.value = false;
                subscriptionCanceled.value = true;
                getCustomerSubscriptionInfo(props.customerId);
            }
        });
}

async function undoCancelSubscription() {
    loading.value = true;

    await fetch(`/Umbraco/Api/Subscription/UndoCancelSubscription?customerId=${props.customerId}&subscriptionId=${csi.value.subscriptionId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                paymentMethodUpdated.value = false;
                subscriptionCanceled.value = false;
                subscriptionCancelationUndone.value = true;
                getCustomerSubscriptionInfo(props.customerId);
            }
        });
}

onMounted(() => {
    stripe = Stripe('pk_live_51QU5E0G2qiSs4LB91zeSt5aFPT18huAxhQkf1pnqHRF9N9FJRvzWw0BnnmYTPNfiytJZ3ndu8U3VNZFekk2msPqh00oC8Rtwwg');

    if (props.customerId !== null) {
        getCustomerSubscriptionInfo(props.customerId);
    } else {
        loading.value = false;
    }

    userEmail.value = document.body.hasAttribute('data-user') ? document.body.getAttribute('data-user') : null;

    const urlParams = new URLSearchParams(window.location.search);
    paymentMethodUpdated.value = urlParams.get('pmUpdated') === 'true';

    if (paymentMethodUpdated.value) {
        const sid = urlParams.get('sid');
        setCustomerDefaultPaymethod(sid);
    }
});
</script>
<template>
    <template v-if="loading">
        <article v-if="csi" class="text module module--white h--pad--wide">
            <div v-if="csi.hasSubscription" class="text__inner">
                <h2>Vent venligst, mens vi henter dine abonnementsoplysninger</h2>
            </div>
        </article>
    </template>
    <template v-else>
        <article class="text module module--white h--pad--wide">
            <div class="text__inner">
                <h2>Du er logget ind som {{ memberName }}</h2>
                <p>
                    Din email-adresse: {{ userEmail }}
                </p>
                <template v-if="csi && csi.hasSubscription">
                    <p v-if="subscriptionCanceled" class="info info--success">
                        Dit abonnement er nu opsagt.
                    </p>
                    <p v-if="subscriptionCancelationUndone" class="info info--success">
                        Opsigelsen af dit abonnement er nu annulleret.
                    </p>
                    <p v-if="paymentMethodUpdated && defaultPaymentMethodUpdated" class="info info--success">
                        Betalingsoplysningerne er nu opdateret.
                    </p>
                    <h2>Tak for, at du har tegnet abonnement p&aring; Kulturen.NU</h2>
                    <p>
                        <b>Dit abonnement, oprettet den {{ new Date(csi.subscriptionStartDate).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}:</b>
                        <br />
                        <template v-if="csi.subscriptionStatus == 'trialing'">
                            Gratis abonnement indtil {{ new Date(csi.subscriptionNextRenewDate).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}
                            <br />
                            Herefter {{ csi.subscriptionName.replace('Abonnement - ', '') }}
                        </template>
                        <template v-else>
                            {{ csi.subscriptionName }}
                        </template>
                    </p>
                    <p v-if="csi.subscriptionStatus != 'trialing' && csi.cancelAt == null && !csi.cancelAtPeriodEnd">
                        Dit abonnement blev senest fornyet den {{ new Date(csi.subscriptionLatestRenewDate).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}
                        <br />
                        Dit abonnement fornyes n&aelig;ste gang den {{ new Date(csi.subscriptionNextRenewDate).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}
                    </p>
                    <p v-if="csi.cancelAt != null || csi.cancelAtPeriodEnd" class="info info--warning">
                        Du har fortsat abonnement indtil den {{ new Date(csi.cancelAt).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}
                        <br />
                        Hvis du har lyst, kan du tegne et kortere abonnement derefter.
                        <br />
                        <br />
                        Hvis du har fortudt opsigelsen af dit abonnement, s&aring; kan du stadig n&aring; at genoptage det.
                        <br />
                        <br />
                        <a @click="undoCancelSubscription()">Gentoptag abonnement</a>
                    </p>
                    <p>
                        <b>Dit abonnement betales med {{ csi.cardBrand }}</b>
                        <br />
                        **** **** **** {{ csi.cardLastFour }}
                        <br />
                        {{ csi.cardExpirationMonth.toString().padStart(2, '0') }} / {{ csi.cardExpirationYear.toString().substring(2) }}
                    </p>
                    <p>
                        <a @click="updatePaymentMethod()">Ændre betalingsoplysninger</a>
                    </p>
                    <p v-if="csi.cancelAt == null && !csi.cancelAtPeriodEnd">
                        Du kan opsige dit abonnement til hver en tid, men det l&oslash;ber indtil enden af tegnet abonnementsperiode.
                        <br />
                        <a @click="cancelSubscription()">Opsig abonnement</a>
                    </p>
                </template>
            </div>
        </article>
    </template>
</template>
